import { apiT, K } from '../apiServiceI18n';

import { parseCustomUrlQuery } from '../apiServiceApiHelpers';

const NO_LOG_MARKERS = new Set([
  'NO_SERVICE_LOG',
  'no service log',
  'n/a',
  'na',
]);

/**
 * Map api_service_list response to table rows.
 */
export function mapApiServiceListResponse(payload) {
  const ok =
    payload?.current_state === 'SUCCESS' ||
    payload?.status === 'SUCCESS';
  if (!ok) return { rows: [], totalCount: 0 };

  const data = payload?.data ?? {};
  const rawRows = Array.isArray(data.rows) ? data.rows : [];
  const totalCount = data.total_count ?? rawRows.length;

  const rows = rawRows.map(mapApiServiceRow);
  return { rows, totalCount };
}

function mapApiServiceRow(r) {
  const enabled = r.enabled === true || r.enabled === 'true';
  const remaining = Number(r.remaining) || 0;
  const executing = remaining > 0;

  let lastExecuted = '';
  let lastExecutedRaw = '';

  if (executing) {
    lastExecutedRaw = `Executing ${remaining}`;
    lastExecuted = lastExecutedRaw;
  } else {
    lastExecutedRaw = r.last_run_time;
    lastExecuted = formatLastRunTime(r.last_run_time);
  }

  const version =
    r.revision_id != null && r.revision_id !== '' ? String(r.revision_id) : '—';

  return {
    id:
      r.service_key ??
      `${r.account}::${r.wf_group}::${r.wf_name}::${r.adapter_type}`,
    name: r.service_name ?? r.wf_name ?? '',
    status: enabled ? 'enabled' : 'disabled',
    workflowGroup: r.wf_group ?? '',
    workflowName: r.wf_name ?? '',
    adapter: r.adapter_type ?? '',
    authorization: r.authenticate ?? '—',
    createdTs: Number(r.created_ts) || 0,
    createdDate: formatCreatedTs(r.created_ts),
    lastExecuted,
    lastExecutedRaw,
    version,
    account: r.account ?? '',
    executingCount: executing ? remaining : null,
    serviceKey: r.service_key,
    baseUrl: r.base_url,
    customUrl: r.custom_url,
    details: r.details,
  };
}

export function formatLastRunTime(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && value > 0) {
    return formatDateTime(new Date(value));
  }
  const str = String(value).trim();
  if (NO_LOG_MARKERS.has(str) || NO_LOG_MARKERS.has(str.toLowerCase())) return '';
  if (/^\d+$/.test(str)) {
    const n = Number(str);
    if (n > 0) return formatDateTime(new Date(n));
  }
  return str;
}

function formatCreatedTs(value) {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return formatDateTime(new Date(n));
}

function formatDateTime(date) {
  try {
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return String(date);
  }
}

/** Build api_service_list query params from portal account + applied filters + page. */
export function buildApiServiceListParams(baseAccount, appliedFilters, page = 0) {
  const isAllMode = baseAccount?.toLowerCase() === 'all';
  const account = isAllMode ? '*' : baseAccount;
  const limit = appliedFilters.pageSize ?? 50;
  const safePage = Math.max(0, page);

  const params = {
    account,
    offset: safePage * limit,
    limit,
  };

  if (appliedFilters.name?.trim()) {
    params.search = appliedFilters.name.trim();
  }

  if (
    isAllMode &&
    appliedFilters.accountFilter &&
    appliedFilters.accountFilter !== 'ALL'
  ) {
    params.filter_account = appliedFilters.accountFilter;
  }

  if (appliedFilters.workflowGroup && appliedFilters.workflowGroup !== 'ALL') {
    params.wfg_name = appliedFilters.workflowGroup;
  }

  if (appliedFilters.workflow) {
    params.wf_name = appliedFilters.workflow;
  }

  if (appliedFilters.status && appliedFilters.status !== 'ALL') {
    params.enabled = appliedFilters.status === 'enabled' ? 'true' : 'false';
  }

  if (appliedFilters.adapter && appliedFilters.adapter !== 'ALL') {
    params.adapter_type = appliedFilters.adapter;
  }

  return params;
}

/** Unique sorted values for filter dropdowns (from loaded rows). */
export function buildFilterOptions(rows) {
  const workflowGroups = new Set();
  const workflows = new Set();
  const adapters = new Set();

  for (const row of rows) {
    if (row.workflowGroup) workflowGroups.add(row.workflowGroup);
    if (row.workflowName) workflows.add(row.workflowName);
    if (row.adapter) adapters.add(row.adapter);
  }

  return {
    workflowGroups: [...workflowGroups].sort((a, b) => a.localeCompare(b)),
    workflows: [...workflows].sort((a, b) => a.localeCompare(b)),
    adapters: [...adapters].sort((a, b) => a.localeCompare(b)),
  };
}

export const DEFAULT_API_SERVICE_FILTERS = {
  name: '',
  accountFilter: 'ALL',
  workflowGroup: 'ALL',
  workflow: '',
  status: 'enabled',
  adapter: 'ALL',
  pageSize: 25,
};

export const PAGE_SIZE_OPTIONS = [25, 50, 75, 100];

/** 0-based page index from total count and page size (clamps to last valid page). */
export function clampPageIndex(page, totalCount, pageSize) {
  if (totalCount <= 0) return 0;
  const limit = pageSize > 0 ? pageSize : 50;
  const maxPage = Math.max(0, Math.ceil(totalCount / limit) - 1);
  return Math.min(Math.max(0, page), maxPage);
}

/** Human-readable range for the current page. */
export function getPageRange(page, pageSize, totalCount) {
  if (totalCount <= 0) {
    return { start: 0, end: 0, totalPages: 0, page: 0 };
  }
  const limit = pageSize > 0 ? pageSize : 50;
  const totalPages = Math.ceil(totalCount / limit);
  const safePage = clampPageIndex(page, totalCount, limit);
  const start = safePage * limit + 1;
  const end = Math.min((safePage + 1) * limit, totalCount);
  return { start, end, totalPages, page: safePage };
}

export const EXEC_HISTORY_ROW_COUNT = 50;

/** Map get_service_exec_history response (supports doc + legacy ServiceLog field names). */
export function mapServiceExecHistoryResponse(payload) {
  const ok =
    payload?.current_state === 'SUCCESS' ||
    payload?.status === 'SUCCESS';
  if (!ok) {
    return { rows: [], error: payload?.message || apiT(K.API_SVC_EXEC_HISTORY_FAILED) };
  }

  const raw = payload?.data;
  const list = Array.isArray(raw) ? raw : [];
  return { rows: list.map(mapServiceExecHistoryRow), error: null };
}

function mapServiceExecHistoryRow(r) {
  return {
    timestamp: r.bg_timestamp ?? r.logDate ?? r.dateTimestamp ?? r.date ?? '',
    status: r.status ?? r.statusDetails ?? '',
    transactionIds: normalizeTransactionIds(
      r.transaction_ids ?? r.transIds ?? r.transactions ?? r.transactionIds
    ),
    serviceName: r.service_name ?? r.serviceName ?? r.name ?? '',
    workflowGroup: r.wf_group ?? r.wfGroup ?? '',
    workflowName: r.wf_name ?? r.wfName ?? '',
  };
}

function normalizeTransactionIds(value) {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof value === 'number') return [String(value)];
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

export function formatExecHistoryDate(value) {
  if (value == null || value === '') return '—';
  if (value instanceof Date) return formatDateTime(value);
  const raw = String(value).trim();
  if (!raw) return '—';
  const ts = Number(raw);
  if (!Number.isNaN(ts) && ts > 0) {
    return formatDateTime(new Date(ts));
  }
  return raw;
}

export function formatExecHistoryStatus(value) {
  const raw = value == null ? '' : String(value).trim();
  if (!raw) return '—';
  const decoded = raw
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\+/g, ' ');
  try {
    return decodeURIComponent(decoded.replace(/\+/g, '%20'));
  } catch {
    return decoded;
  }
}

export {
  buildTransactionSearchFormFields,
  formatTxnSearchDate,
  resolveBridgegateRoot,
  submitTransactionSearch,
} from './portalUrlUtils';

/** Base URL preview aligned with Get Data properties: /execute/{account}/{workflowGroup}/{workflowName}. */
export function buildApiServiceBaseUrlPreview({ account, workflowGroup, workflowName, pathBase }) {
  if (pathBase) {
    let path = String(pathBase).startsWith('/') ? pathBase : `/${pathBase}`;
    if (!/\/execute(\/|$)/i.test(path)) {
      path = `/execute${path}`;
    }
    return `https://[SERVER_URL]${path}`;
  }
  const acc = account || '[account]';
  const wg = (workflowGroup || '[workflow_group]').trim().replace(/\s+/g, '_');
  const wf = (workflowName || '[workflow_name]').trim().replace(/\s+/g, '_');
  return `https://[SERVER_URL]/execute/${encodeURIComponent(acc)}/${encodeURIComponent(wg)}/${encodeURIComponent(wf)}`;
}

export function resolveServiceAccount(reduxAccount, rowAccount) {
  const fromRedux = String(reduxAccount ?? '').trim();
  if (fromRedux && fromRedux.toLowerCase() !== 'all') return fromRedux;
  const fromRow = String(rowAccount ?? '').trim();
  if (fromRow && fromRow.toLowerCase() !== 'all') return fromRow;
  return '';
}

/** Map api_service_get_detail response. */
export function mapApiServiceDetailResponse(payload) {
  const ok =
    payload?.current_state === 'SUCCESS' ||
    payload?.status === 'SUCCESS';
  if (!ok) {
    return { detail: null, error: payload?.message || apiT(K.API_SVC_DETAILS_FAILED) };
  }

  const data = payload?.data ?? {};
  const conformance = data.conformance || {};

  const version =
    data.revision_id != null && data.revision_id !== ''
      ? String(data.revision_id)
      : '';

  return {
    detail: {
      account: data.account ?? '',
      workflowGroup: data.wf_group ?? '',
      workflowName: data.wf_name ?? '',
      name: data.service_name ?? data.wf_name ?? '',
      description: data.service_description ?? '',
      workflowComments: data.workflow_comments ?? '',
      adapter: data.adapter_type ?? '',
      authorization: data.authenticate ?? '',
      baseUrl: data.base_url ?? '',
      customUrl: data.custom_url ?? '',
      customUrlParams: parseCustomUrlQuery(data.custom_url_query),
      enabled: data.enabled === true || data.enabled === 'true',
      remaining: data.remaining ?? '',
      clusterDetails: normalizeClusterDetailsHtml(data.details),
      lastRunTime: data.last_run_time,
      version,
      conformanceMs: Number(conformance.execute_within_ms) || 0,
      conformanceErrorsOnly: conformance.errors_only === true || conformance.errors_only === 'true',
    },
    error: null,
  };
}

/** Operations > Services strips JSON quotes from remaining-count detail HTML. */
export function normalizeClusterDetailsHtml(value) {
  if (value == null || value === '') return '';
  let text = String(value).trim();
  if (
    (text.startsWith('"') && text.endsWith('"')) ||
    (text.startsWith("'") && text.endsWith("'"))
  ) {
    text = text.slice(1, -1);
  }
  return text
    .replace(/\\"/g, '"')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t');
}

/**
 * Map api_service_get_detail response `detail` object → wizard-compatible form state.
 * Used to pre-populate the Edit / View API screens.
 */
export function mapApiServiceDetailToForm(detail) {
  if (!detail) return null;

  // Convert conformanceMs → human-readable value + unit
  let executeWithin = '';
  let executeUnit = 'ms';
  const ms = detail.conformanceMs || 0;
  if (ms > 0) {
    if (ms % 60000 === 0) {
      executeWithin = String(ms / 60000);
      executeUnit = 'min';
    } else if (ms % 1000 === 0) {
      executeWithin = String(ms / 1000);
      executeUnit = 'sec';
    } else {
      executeWithin = String(ms);
      executeUnit = 'ms';
    }
  }

  return {
    apiName: detail.name || '',
    description: detail.description || '',
    apiType: detail.adapter || '',
    workflowGroup: detail.workflowGroup || '',
    workflowName: detail.workflowName || '',
    conformance: ms > 0 || detail.conformanceErrorsOnly,
    conformanceExecute: ms > 0,
    executeWithin,
    executeUnit,
    hasNoErrors: detail.conformanceErrorsOnly || false,
    customUrl: {
      from: detail.customUrl || '',
      params: detail.customUrlParams || [],
    },
    enabled: detail.enabled,
  };
}

export function formatConformanceSummary(detail, t) {
  if (!detail?.conformanceMs) return t(K.API_SVC_EM_DASH);
  const unit =
    detail.conformanceMs >= 60000
      ? t(K.API_SVC_EXECUTE_WITHIN_MIN)
      : detail.conformanceMs >= 1000
        ? t(K.API_SVC_EXECUTE_WITHIN_SEC)
        : t(K.API_SVC_EXECUTE_WITHIN_MS);
  const value =
    detail.conformanceMs >= 60000
      ? Math.round(detail.conformanceMs / 60000)
      : detail.conformanceMs >= 1000
        ? Math.round(detail.conformanceMs / 1000)
        : detail.conformanceMs;
  const suffix = detail.conformanceErrorsOnly ? ` (${t(K.API_SVC_HAS_NO_ERRORS)})` : '';
  return `${value} ${unit}${suffix}`;
}
